/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var logPrefixes = {
    debug: [],
    info: [],
    warn: [],
    error: [],
};
/**
 * Set prefixes that will be prepended to all log messages. This can be helpful to quickly identify relevant messages
 * in an integration scenario, e.g. with multiple iframes all spamming console messages.
 * @param customLogPrefixes Either an Array of strings or a LogPrefixMap with different prefixes for some or all of the
 * logging methods.
 *
 * Examples:
 * consoleAppender.setLogPrefixes(['MyApp']) // one prefix for all
 * debug('foo') // logs MyApp foo
 * error('bar') // logs MyApp bar
 *
 * consoleAppender.setLogPrefixes({
 *   debug: ['MyApp', 'DEBUG'],
 *   info: ['MyApp', 'INFO'],
 * })
 * debug('foo') // logs MyApp DEBUG foo
 * info('bar') // logs MyApp INFO bar
 * error('baz') // logs baz
 */
function setLogPrefixes(customLogPrefixes) {
    if (customLogPrefixes instanceof Array) {
        logPrefixes.debug = customLogPrefixes;
        logPrefixes.info = customLogPrefixes;
        logPrefixes.warn = customLogPrefixes;
        logPrefixes.error = customLogPrefixes;
    }
    else {
        logPrefixes.debug = customLogPrefixes.debug || [];
        logPrefixes.info = customLogPrefixes.info || [];
        logPrefixes.warn = customLogPrefixes.warn || [];
        logPrefixes.error = customLogPrefixes.error || [];
    }
}
var consoleAppender = {
    debug: function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        console.debug.apply(console, __spreadArray(__spreadArray([], logPrefixes.debug, false), args, false));
    },
    info: function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        console.info.apply(console, __spreadArray(__spreadArray([], logPrefixes.info, false), args, false));
    },
    warn: function (message, error, data) {
        console.warn.apply(console, __spreadArray(__spreadArray([], logPrefixes.warn, false), [message, error, data], false));
    },
    error: function (message, error, data) {
        console.error.apply(console, __spreadArray(__spreadArray([], logPrefixes.error, false), [message, error, data], false));
    },
    setLogPrefixes: setLogPrefixes,
};

var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["debug"] = 0] = "debug";
    LogLevel[LogLevel["info"] = 1] = "info";
    LogLevel[LogLevel["warn"] = 2] = "warn";
    LogLevel[LogLevel["error"] = 3] = "error";
    LogLevel[LogLevel["none"] = 4] = "none";
})(LogLevel || (LogLevel = {}));
var appenders = [consoleAppender];
function registerAppender(appender) {
    appenders.push(appender);
}
var _logLevel = LogLevel.debug;
function setLogLevel(logLevel) {
    _logLevel = logLevel;
}
function debug() {
    var args = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
    }
    /* istanbul ignore next else */
    if (_logLevel <= LogLevel.debug) {
        appenders
            .filter(function (appender) { return typeof appender.debug === 'function'; })
            .forEach(function (appender) {
            appender.debug.apply(appender, args);
        });
    }
}
function info() {
    var args = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
    }
    /* istanbul ignore next else */
    if (_logLevel <= LogLevel.info) {
        appenders
            .filter(function (appender) { return typeof appender.info === 'function'; })
            .forEach(function (appender) {
            appender.info.apply(appender, args);
        });
    }
}
function warn(message, error, data) {
    /* istanbul ignore next else */
    if (_logLevel <= LogLevel.warn) {
        appenders
            .filter(function (appender) { return typeof appender.warn === 'function'; })
            .forEach(function (appender) {
            appender.warn(message, error, data);
        });
    }
}
function error(message, error, data) {
    /* istanbul ignore next else */
    if (_logLevel <= LogLevel.error) {
        appenders
            .filter(function (appender) { return typeof appender.error === 'function'; })
            .forEach(function (appender) {
            appender.error(message, error, data);
        });
    }
}

// use the global Sentry namespace if available (we don't want to bundle Sentry with this library)
/* istanbul ignore next */
var getGlobalSentry = function () {
    var _a, _b, _c;
    return {
        captureException: ((_a = window.Sentry) === null || _a === void 0 ? void 0 : _a.captureException) || function () { },
        captureMessage: ((_b = window.Sentry) === null || _b === void 0 ? void 0 : _b.captureMessage) || function () { },
        withScope: ((_c = window.Sentry) === null || _c === void 0 ? void 0 : _c.withScope) || function () { },
    };
};
/* istanbul ignore next */
var fillScope = function (scope, data) {
    if (data === null || data === void 0 ? void 0 : data.context) {
        scope.setContext(data.context.name, data.context.value);
    }
    if (data === null || data === void 0 ? void 0 : data.extras) {
        scope.setExtras(data.extras);
    }
    if (data === null || data === void 0 ? void 0 : data.level) {
        scope.setLevel(data.level);
    }
};
var log = function (message, error, data) {
    var Sentry = getGlobalSentry();
    Sentry.withScope(function (scope) {
        fillScope(scope, data);
        if (error instanceof Error) {
            try {
                // Error.prototype.name is not writable in Safari and Firefox sometimes
                /* istanbul ignore next line */
                error.name = message ? "".concat(message, ": ").concat(error.name) : error.name;
                // eslint-disable-next-line no-empty
            }
            catch (e) { }
            Sentry.captureException(error);
        }
        else if (typeof (error === null || error === void 0 ? void 0 : error.toString) === 'function') {
            Sentry.captureMessage("".concat(message, ": ").concat(error.toString()));
        }
        else {
            Sentry.captureMessage(message);
        }
    });
};
/**
 * Log appender that writes to Sentry
 */
var sentryAppender = {
    warn: function (message, error, data) {
        log(message, error, __assign(__assign({}, data), { level: 'warning' }));
    },
    error: function (message, error, data) {
        log(message, error, __assign(__assign({}, data), { level: 'error' }));
    },
};

export { LogLevel, consoleAppender, debug, error, info, registerAppender, sentryAppender, setLogLevel, setLogPrefixes, warn };
//# sourceMappingURL=frontend-logger.mjs.map
